import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import './AppBaseDropdown.css';
import {
  getDropdownDirectionStyles,
  getDropdownWidth,
  type XDirectionMode,
  type YDirectionMode,
} from './helpers';
import { CSS_CLASSES, DEBOUNCE_DELAY } from './constants';
import {
  createFallbackStyles,
  createFinalDropdownStyles,
  createInitialDropdownStyles,
  getContainerRect,
  getDropdownElement,
  nextFrame,
} from './utils';
import { useDebouncedCallback } from './hooks/useDebouncedCallback';
import { useDropdownState } from './hooks/useDropdownState';
import { useEscapeKey } from './hooks/useEscapeKey';
import { useLatest } from './hooks/useLatest';
import { useResizeObserver } from './hooks/useResizeObserver';
import { useWindowEvent } from './hooks/useWindowEvent';
import { usePlatform } from '@pro-sto/shared/hooks';
import { MobileDrawer } from './MobileDrawer';

export interface AppBaseDropdownProps {
  opened: boolean;
  container?: Element | null;
  toggleWidth?: string;
  dropdownWidth?: 'min-toggle' | 'equal-toggle' | 'none' | string;
  xDirection?: XDirectionMode;
  yDirection?: YDirectionMode;
  maxDropdownHeight?: number | string;
  noRestrictHeigth?: boolean;
  onClose?: () => void;
  toggle: ReactNode;
  dropdown: ReactNode;
  /** Force mobile drawer mode (useful for specific components like DatePicker) */
  forceMobileDrawer?: boolean;
  /** Max height for mobile drawer (e.g. "50vh", "400px"), default "90vh" */
  mobileDrawerMaxHeight?: string;
  /** If true, mobile drawer will have fixed height instead of dynamic */
  mobileDrawerFixedHeight?: boolean;
  /** data-testid для тестирования */
  'data-testid'?: string;
}

export const AppBaseDropdown: React.FC<AppBaseDropdownProps> = ({
  opened,
  container = null,
  toggleWidth = 'auto',
  dropdownWidth = 'min-toggle',
  xDirection = 'free-space',
  yDirection = 'free-space',
  maxDropdownHeight,
  noRestrictHeigth = false,
  onClose,
  toggle,
  dropdown,
  forceMobileDrawer = false,
  mobileDrawerMaxHeight = '90vh',
  mobileDrawerFixedHeight = false,
  'data-testid': dataTestId,
}) => {
  const platform = usePlatform();
  const isMobileMode = platform === 'mobile' || forceMobileDrawer;
  
  const rootRef = useRef<HTMLDivElement | null>(null);
  const toggleRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const overflowRef = useRef<HTMLDivElement | null>(null);

  const [state, actions] = useDropdownState();
  const onCloseRef = useLatest(onClose);

  const preparedContainer = useMemo(() => {
    if (container) return container;
    const root = rootRef.current;
    const parent = root?.closest('.dropdown-container');
    return parent || document.body;
  }, [container]);

  const preparedMaxDropdownHeight = useMemo(() => {
    if (maxDropdownHeight === undefined) return undefined;
    return Number.parseFloat(String(maxDropdownHeight));
  }, [maxDropdownHeight]);

  const close = useCallback(() => {
    actions.reset();
    onCloseRef.current?.();
  }, [actions, onCloseRef]);

  // Wheel must use a non-passive native listener — React onWheel is passive and preventDefault() warns.
  useEffect(() => {
    const toggleEl = toggleRef.current;
    if (!toggleEl || !state.localOpened) return;

    const handleWheelScroll = (event: WheelEvent) => {
      event.preventDefault();
      event.stopPropagation();
    };

    toggleEl.addEventListener('wheel', handleWheelScroll, { passive: false });
    return () => toggleEl.removeEventListener('wheel', handleWheelScroll);
  }, [state.localOpened]);

  // Фаза 1 открытия: синхронно, из обработчика клика/эффекта, считаем размеры
  // тумблера и монтируем дропдаун скрытым (visibility: hidden) — его ещё
  // нужно один раз отрендерить в DOM, чтобы во 2-й фазе (useLayoutEffect
  // ниже) измерить его реальные размеры и посчитать позицию.
  const beginOpen = useCallback(() => {
    const toggleEl = toggleRef.current;
    if (!toggleEl) return;

    const toggleRect = toggleEl.getBoundingClientRect();
    actions.setToggleHeight(toggleRect.height);

    const dropdownWidthStyle = getDropdownWidth({
      dropdownWidthMode: dropdownWidth,
      toggleRect,
    });
    actions.setDropdownStyle(createInitialDropdownStyles(state.dropdownStyle, dropdownWidthStyle));
    actions.setLocalOpened(true);
  }, [dropdownWidth, state.dropdownStyle, actions]);

  // Фаза 2 открытия: как только дропдаун смонтирован скрытым
  // (state.localOpened стал true), useLayoutEffect синхронно — ДО пейнта —
  // измеряет его и выставляет финальные позицию/видимость. Раньше здесь
  // стоял `await requestAnimationFrame`, из-за которого между кликом и
  // реальным появлением дропдауна был разрыв в 1+ кадр: быстрый повторный
  // клик по тумблеру успевал переключить opened обратно на false раньше, чем
  // дропдаун хоть раз стал видимым ("по быстрому клику не открывается").
  // useLayoutEffect — штатный React-механизм именно для этого (в отличие от
  // flushSync его нельзя случайно вызвать посреди уже идущего рендера).
  useLayoutEffect(() => {
    if (!state.localOpened) return;

    const toggleEl = toggleRef.current;
    const dropdownEl = dropdownRef.current;
    if (!toggleEl) return;

    if (!dropdownEl) {
      actions.setDropdownStyle(prev => createFallbackStyles(prev));
      return;
    }

    const toggleRect = toggleEl.getBoundingClientRect();
    const containerRect = getContainerRect(preparedContainer);
    const dropdownRect = dropdownEl.getBoundingClientRect();
    const windowHeight = document.documentElement.clientHeight;
    const windowWidth = document.documentElement.clientWidth;

    const directionStyles = getDropdownDirectionStyles({
      dropdownRect,
      toggleRect,
      containerRect,
      windowWidth,
      windowHeight,
      xDirectionMode: xDirection,
      yDirectionMode: yDirection,
      maxDropdownHeight: preparedMaxDropdownHeight,
      noRestrictHeigth,
      fixedXDirection: undefined,
      fixedYDirection: undefined,
    });

    actions.setDropdownStyle(prev => createFinalDropdownStyles(prev, directionStyles.styles));
    actions.setDirections({
      x: directionStyles.xDirection,
      y: directionStyles.yDirection,
    });
    // Должен сработать ровно один раз на каждое открытие — именно в момент,
    // когда state.localOpened становится true (дропдаун только что
    // смонтирован скрытым). Остальные значения (xDirection и т.п.) читаются
    // из актуального замыкания на момент срабатывания, пересчитывать позицию
    // при их изменении "на лету" (пока уже открыт) — отдельная задача
    // (см. recalculateWithDirection/resize ниже).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.localOpened]);

  const recalculateWithDirection = useCallback(async () => {
    const toggleEl = toggleRef.current;
    if (!toggleEl) return;

    const toggleRect = toggleEl.getBoundingClientRect();
    const containerRect = getContainerRect(preparedContainer);

    await nextFrame();
    const dropdownEl = await getDropdownElement(dropdownRef);
    if (!dropdownEl) return;

    const dropdownRect = dropdownEl.getBoundingClientRect();
    const windowHeight = document.documentElement.clientHeight;
    const windowWidth = document.documentElement.clientWidth;

    const directionStyles = getDropdownDirectionStyles({
      dropdownRect,
      toggleRect,
      containerRect,
      windowWidth,
      windowHeight,
      xDirectionMode: xDirection,
      yDirectionMode: yDirection,
      maxDropdownHeight: preparedMaxDropdownHeight,
      noRestrictHeigth,
      fixedXDirection: state.directions.x,
      fixedYDirection: state.directions.y,
    });

    const finalStyles = createFinalDropdownStyles(
      state.dropdownStyle,
      directionStyles.styles
    );
    actions.setDropdownStyle(finalStyles);
  }, [
    preparedContainer,
    xDirection,
    yDirection,
    preparedMaxDropdownHeight,
    noRestrictHeigth,
    state.directions,
    state.dropdownStyle,
    actions,
  ]);

  const debouncedRecalculate = useDebouncedCallback(recalculateWithDirection, DEBOUNCE_DELAY);

  const handleToggleDimensionChange = useCallback(() => {
    if (!state.localOpened) return;
    const h = toggleRef.current?.getBoundingClientRect().height;
    if (h === undefined || h === state.toggleHeight) return;
    recalculateWithDirection();
  }, [state.localOpened, state.toggleHeight, recalculateWithDirection]);

  const handleWindowResize = useCallback(() => {
    if (!state.localOpened) return;
    actions.setIsWindowResizing(true);
    debouncedRecalculate();
  }, [state.localOpened, debouncedRecalculate, actions]);

  // Скролл идёт внутри layout, а не window. Без пересчёта position:fixed
  // остаётся на старых координатах, и список отрывается от селекта.
  const repositionOnScroll = useCallback((event: Event) => {
    if (isMobileMode) return;
    const toggleEl = toggleRef.current;
    const dropdownEl = dropdownRef.current;
    if (!toggleEl || !dropdownEl) return;
    if (event.target instanceof Node && dropdownEl.contains(event.target)) return;

    const toggleRect = toggleEl.getBoundingClientRect();
    const centerX = toggleRect.left + toggleRect.width / 2;
    const centerY = toggleRect.top + toggleRect.height / 2;
    const offScreen =
      centerX < 0 ||
      centerY < 0 ||
      centerX > window.innerWidth ||
      centerY > window.innerHeight;
    const coveredBy = document.elementFromPoint(centerX, centerY);
    const covered =
      !offScreen &&
      coveredBy !== null &&
      !toggleEl.contains(coveredBy) &&
      !dropdownEl.contains(coveredBy);
    if (offScreen || covered) {
      close();
      return;
    }

    const root = rootRef.current;
    const explicitContainer = container;
    const parent = root?.closest('.dropdown-container');
    const resolvedContainer = explicitContainer || parent || document.body;
    const containerRect = getContainerRect(resolvedContainer);
    const dropdownRect = dropdownEl.getBoundingClientRect();
    const directionStyles = getDropdownDirectionStyles({
      dropdownRect,
      toggleRect,
      containerRect,
      windowWidth: document.documentElement.clientWidth,
      windowHeight: document.documentElement.clientHeight,
      xDirectionMode: xDirection,
      yDirectionMode: yDirection,
      maxDropdownHeight: preparedMaxDropdownHeight,
      noRestrictHeigth,
      fixedXDirection: state.directions.x,
      fixedYDirection: state.directions.y,
    });

    actions.setDropdownStyle((prev) => createFinalDropdownStyles(prev, directionStyles.styles));
    actions.setDirections({
      x: directionStyles.xDirection,
      y: directionStyles.yDirection,
    });
  }, [
    actions,
    container,
    isMobileMode,
    noRestrictHeigth,
    preparedMaxDropdownHeight,
    close,
    state.directions.x,
    state.directions.y,
    xDirection,
    yDirection,
  ]);

  const handleOutsideClick = useCallback((e: MouseEvent) => {
    const target = e.target as Element;
    const root = rootRef.current;
    const clickOutside = !root?.contains(target) || target === overflowRef.current;
    if (clickOutside) {
      e.stopPropagation();
      close();
    }
  }, [close]);

  // React to opened prop changes
  useEffect(() => {
    if (state.localOpened === opened) return;
    if (opened) {
      beginOpen();
    } else {
      close();
    }
  }, [opened, state.localOpened, beginOpen, close]);

  // Event listeners
  useResizeObserver(toggleRef, handleToggleDimensionChange);
  useWindowEvent('resize', handleWindowResize, undefined, state.localOpened);
  useWindowEvent('scroll', repositionOnScroll, true, state.localOpened && !isMobileMode);
  // Don't handle outside clicks in mobile mode - drawer handles its own closing
  useWindowEvent('mousedown', handleOutsideClick, true, state.localOpened && !isMobileMode);
  useEscapeKey(close, state.localOpened);

  const rootClassName = useMemo(() => [
    CSS_CLASSES.root,
    opened && CSS_CLASSES.rootOpened,
  ].filter(Boolean).join(' '), [opened]);

  // Mobile drawer mode
  if (isMobileMode) {
    return (
      <div ref={rootRef} className={rootClassName} data-testid={dataTestId}>
        <div
          ref={toggleRef}
          className={CSS_CLASSES.toggleWrapper}
          style={{ width: toggleWidth }}
        >
          {toggle}
        </div>

        <MobileDrawer 
          opened={opened} 
          onClose={onClose} 
          maxHeight={mobileDrawerMaxHeight}
          fixedHeight={mobileDrawerFixedHeight}
        >
          {dropdown}
        </MobileDrawer>
      </div>
    );
  }

  // Desktop dropdown mode
  return (
    <div ref={rootRef} className={rootClassName} data-testid={dataTestId}>
      {state.localOpened && (
        <div ref={overflowRef} className={CSS_CLASSES.overflow} />
      )}
      
      <div
        ref={toggleRef}
        className={CSS_CLASSES.toggleWrapper}
        style={{ width: toggleWidth }}
      >
        {toggle}
      </div>

      <div className={CSS_CLASSES.dropdownWrapper}>
        <div className={CSS_CLASSES.contentWrapper}>
          {state.localOpened && !state.isWindowResizing && (
            <div 
              ref={dropdownRef} 
              className={CSS_CLASSES.content} 
              style={state.dropdownStyle}
            >
              {dropdown}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppBaseDropdown;